import { HostPageContent } from '@website/components/hosts/hostPage/hostPage';
import { StaticHostUrlsParams } from '@website/components/hosts/hostPage/types';
import ErrorPage from '@website/components/utilities/ErrorPage';
import LoadingPage from '@website/components/utilities/LoadingPage';
import { ssrClient, useHydrateApolloCache } from '@website/libs/graphql/apollo';
import {
  GetHostsStaticPathsDocument,
  HostPageDetailsDocument,
  HostPageDetailsQuery,
} from '@website/types/graphqlOperations';
import { GraphQLError } from 'graphql';
import {
  GetStaticPathsResult,
  GetStaticPropsContext,
  GetStaticPropsResult,
} from 'next';
import { useRouter } from 'next/router';

type HostPageProps = {
  data: HostPageDetailsQuery;
  errors?: readonly GraphQLError[];
  urlSlug: string;
};

export default function HostPage({ data, errors, urlSlug }: HostPageProps) {
  useHydrateApolloCache({
    data,
    query: HostPageDetailsDocument,
    variables: { urlSlug: data?.hostByURL.urlSlug },
  });

  const router = useRouter();

  // show loading page if the page hasn't been generated, fallback will generate
  // send it to the frontend
  if (router.isFallback) {
    return <LoadingPage />;
  }

  if (errors) {
    <ErrorPage error={errors} />;
  }

  return <HostPageContent urlSlug={urlSlug} />;
}

export async function getStaticProps({
  params,
}: GetStaticPropsContext): Promise<GetStaticPropsResult<HostPageProps>> {
  const { hostId: urlSlug } = params;

  if (!urlSlug) {
    return { notFound: true };
  }

  const client = ssrClient();

  const { data, errors } = await client.query({
    query: HostPageDetailsDocument,
    variables: {
      urlSlug,
    },
    // return both errors and data, instead of throwing, allowing us to decide
    // what to do with the errors
    errorPolicy: 'all',
  });

  // if page is not, show not found page
  if (errors && errors.length > 0) {
    const notFound = !!errors.find((e) => e.message.match(/- not found/i));
    if (notFound) {
      return {
        notFound: true,
      };
    }
  }

  return {
    props: {
      data,
      errors: (errors as any) ?? null,
      urlSlug: urlSlug as string,
    },
    revalidate: 60,
  };
}

export async function getStaticPaths(): Promise<
  GetStaticPathsResult<StaticHostUrlsParams>
> {
  const client = ssrClient();

  const res = await client.query({
    query: GetHostsStaticPathsDocument,
    variables: {
      limit: 200,
    },
  });

  const paths = res.data.hosts.items.map((d) => {
    return { params: { hostId: d.urlSlug } };
  });

  return {
    paths: [...paths],
    fallback: true,
  };
}
